import { ChangeEvent, FC, memo, useEffect, useState } from 'react'
import { Box, Flex, FormControl, FormLabel, Input, InputGroup, InputRightElement, Spinner } from '@chakra-ui/react'

import { slugMask } from 'utils/masks'
import { FormItem } from 'components'
import { CheckCircle, Close } from 'components/icons'
import { focusElementById } from 'utils/events'
import SlugText from './components/SlugText'
import MaskedInput from '../MaskedInput'
import SlugActionsIcons from './components/SlugActionsIcons'

type Props = {
  watch: any
  register: any
  setValue: any
  fieldName?: string
  fieldLabel?: string
  checkSlug: (slug?: string) => Promise<boolean>
}

const SlugInput: FC<Props> = ({ fieldLabel = 'Nome', fieldName = 'name', watch, setValue, checkSlug }) => {
  const [loadingSlugChecking, setLoadingSlugChecking] = useState(false)
  const [isSlugValid, setIsSlugValid] = useState<boolean | null>(null)
  const [context, setContext] = useState('create')
  const [field, setField] = useState(watch(fieldName) || '')
  const [slug, setSlug] = useState(watch('slug') || '')

  useEffect(() => {
    setValue(fieldName, field)
  }, [field])

  useEffect(() => {
    setValue('slug', slug)
  }, [slug])

  function handleChangeInput(value: string) {
    setField(value)
    setSlug(slugMask(value))
  }

  async function handleCheckSlug() {
    if (!slug) return null
    setLoadingSlugChecking(true)
    const slugCondition = await checkSlug(slug)
    if (slugCondition !== null) setIsSlugValid(slugCondition)
    setLoadingSlugChecking(false)
  }

  useEffect(() => {
    focusElementById(fieldName)
  }, [])

  return (
    <Box mb={3} minWidth="100%">
      <FormItem isRequired htmlFor={fieldName} label={fieldLabel}>
        <Input
          autoComplete="on"
          id={fieldName}
          value={field}
          onChange={({ target }) => handleChangeInput(target.value)}
          onBlur={handleCheckSlug}
        />
      </FormItem>

      <Flex p={1} mt={1} gap={2} align="center">
        {context === 'create' ? (
          <>
            <SlugText isSlugValid={isSlugValid} slug={slug} setContext={setContext} />
            {loadingSlugChecking && <Spinner />}
          </>
        ) : (
          <FormControl>
            <Flex pb={1} align="center" gap={2}>
              <FormLabel m={0} htmlFor="slug">
                Slug
              </FormLabel>
              <SlugActionsIcons context="edit" setContext={setContext} />
            </Flex>
            <InputGroup>
              <MaskedInput
                autoFocus
                id="slug"
                mask={slugMask}
                value={slug}
                onChange={({ target }: ChangeEvent<HTMLInputElement>) => setSlug(target.value)}
                onBlur={handleCheckSlug}
              />
              <InputRightElement>
                {loadingSlugChecking ? (
                  <Spinner />
                ) : (
                  <>
                    {isSlugValid !== null && (
                      <>{isSlugValid ? <CheckCircle color="success" /> : <Close color="danger" boxSize={8} />}</>
                    )}
                  </>
                )}
              </InputRightElement>
            </InputGroup>
          </FormControl>
        )}
      </Flex>
    </Box>
  )
}

export default memo(SlugInput)
